<template>
  <hb-basic-page
    :app-bar-color="appBarColorWhite ? 'rgba(255, 255, 255, 1)' : 'transparent'"
    :fill-height="surveys.length === 0"
    :loading="loading"
    :subtle-loading="subtleLoading"
    :title="$t('dashboard.title')"
    name="dashboard-view"
  >
    <v-expand-transition>
      <v-col v-if="!loading && filteredNotifications.length > 0" class="grey lighten-5 pt-16 mb-6 pb-1 pb-md-4 alerts"
             cols="12">
        <v-row>
          <v-col lg="8" md="10" offset-lg="2" offset-md="1" offset-xl="3" xl="6">
            <v-slide-x-reverse-transition group>
              <template v-for="notification in filteredNotifications">
                <notification :key="notification.id" :notification="notification" @read="onNotificationRead"/>
              </template>
            </v-slide-x-reverse-transition>
          </v-col>
        </v-row>
      </v-col>
    </v-expand-transition>

    <v-col
      v-if="!loading && surveys.length > 0"
      ref="contentContainer"
      v-scroll="handleScroll"
      class="pb-10"
      cols="12"
      lg="10"
      md="10"
      offset-lg="1"
      offset-md="1"
      offset-xl="2"
      xl="8"
    >
      <v-row>
        <template v-if="personnelSurveyHasAnswers">
          <v-col cols="12" sm="6">
            <v-card>
              <v-card-title class="mb-5">
                <section-header :style="$vuetify.breakpoint.smAndUp && 'height: 40px;'" icon="mdi mdi-heart-pulse">
                  {{ $t('dashboard.company_fitness_index') }}
                </section-header>
              </v-card-title>
              <v-card-text class="px-sm-16">
                <company-fitness-index-chart
                  v-if="personnelSurvey"
                  :calculation="personnelSurvey.calculation"
                  :loading="calculationLoading"
                  @refresh="calculate"
                />
              </v-card-text>
              <v-card-actions :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : ''">
                <basic-button
                  :block="$vuetify.breakpoint.xsOnly"
                  :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                  :disabled="loading || calculationLoading"
                  :outlined="$vuetify.breakpoint.xsOnly"
                  color="primary"
                  text
                  @click="goToInputMap"
                >
                  <v-icon v-if="$icons" left>mdi mdi-arrow-right</v-icon>
                  {{ $t('dashboard.open_input_map') }}
                </basic-button>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card class="fill-height d-flex flex-column justify-space-between">
              <v-card-title class="mb-5">
                <section-header :style="$vuetify.breakpoint.smAndUp && 'height: 40px;'" icon="mdi mdi-human-queue">
                  {{ $t('dashboard.company_culture') }}
                </section-header>
              </v-card-title>
              <v-card-text class="px-sm-16">
                <company-culture-chart
                  v-if="personnelSurvey"
                  :calculation="personnelSurvey.calculation"
                  :loading="calculationLoading"
                  @refresh="calculate"
                />
              </v-card-text>
              <v-card-actions :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : ''">
                <basic-button
                  :block="$vuetify.breakpoint.xsOnly"
                  :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                  :disabled="loading || calculationLoading"
                  :outlined="$vuetify.breakpoint.xsOnly"
                  color="primary"
                  text
                  @click="goToCulture"
                >
                  <v-icon v-if="$icons" left>mdi mdi-arrow-right</v-icon>
                  {{ $t('dashboard.open_culture_analysis') }}
                </basic-button>
              </v-card-actions>
            </v-card>
          </v-col>
        </template>
        <template v-else>
          <v-col cols="12">
            <v-card class="pt-4 pb-5">
              <no-content-view
                v-if="$hasMatchingTenantRoles(['tenant_admin'])"
                :description="$t('dashboard.personnel_survey_have_no_answers_text')"
                :function-button="true"
                :function-button-text="$t('dashboard.go_to_surveys')"
                :no-bottom-padding="true"
                :prop-function="goToSurveys"
                :reload-button="false"
                :title="$t('dashboard.personnel_survey_have_no_answers')"
                function-button-icon="mdi mdi-arrow-right"
                title-image="img/undraw_customer_survey_re_v9cj.svg"
              />

              <no-content-view
                v-else
                :description="$t('dashboard.personnel_survey_have_no_answers_text_basic')"
                :no-bottom-padding="true"
                :reload-button="false"
                :title="$t('dashboard.personnel_survey_have_no_answers')"
                title-image="img/undraw_customer_survey_re_v9cj.svg"
              />
            </v-card>
          </v-col>
        </template>

        <v-col cols="12">
          <v-card>
            <v-card-title class="mb-5">
              <section-header icon="mdi mdi-format-list-checks">
                {{ $t('dashboard.tasks') }}
              </section-header>
            </v-card-title>
            <v-card-text class="text-body-2 text-sm-body-1 pb-0">
              <tasks-overview ref="tasksOverview" @refresh="handleRefresh"/>
            </v-card-text>
            <v-card-actions :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : ''">
              <basic-button
                v-if="totalTasksCount === 0 || undoneTasksCount > 0"
                :block="$vuetify.breakpoint.xsOnly"
                :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                :disabled="loading || calculationLoading"
                :outlined="$vuetify.breakpoint.xsOnly"
                color="primary"
                text
                @click="goToTasks"
              >
                <v-icon v-if="$icons" left>mdi mdi-arrow-right</v-icon>
                {{ $t('dashboard.open_tasks') }}
              </basic-button>
              <basic-button
                v-if="totalTasksCount > 0 && undoneTasksCount === 0 && $hasMatchingTenantRoles(['tenant_admin'])"
                :block="$vuetify.breakpoint.xsOnly"
                :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                :disabled="loading || calculationLoading"
                :outlined="$vuetify.breakpoint.xsOnly"
                color="primary"
                text
                @click="goToSurveys"
              >
                <v-icon v-if="$icons" left>mdi mdi-arrow-right</v-icon>
                {{ $t('dashboard.go_to_surveys') }}
              </basic-button>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col v-if="$isPaidTenant" cols="12">
          <financial-development-chart/>
        </v-col>
      </v-row>
    </v-col>

    <v-col
      v-if="!loading && surveys.length === 0"
      class="pa-0 fill-height"
      cols="12"
      lg="8"
      md="10"
      offset-lg="2"
      offset-md="1"
      offset-xl="3"
      xl="6"
    >
      <v-row align="center" class="fill-height" no-gutters>
        <no-content-view
          v-if="$isTenantAdmin"
          :description="$t('dashboard.dashboard_empty_text')"
          :description-extra="$t('dashboard.dashboard_empty_text2')"
          :function-button="true"
          :function-button-text="$t('dashboard.go_to_surveys')"
          :prop-function="goToSurveys"
          :reload-button="false"
          :title="$t('dashboard.dashboard_empty_title')"
          function-button-icon="mdi mdi-arrow-right"
          title-image="img/undraw_maker_launch_re_rq81.svg"
        />
        <no-content-view
          v-else
          :description="$t('dashboard.dashboard_empty_text_user')"
          :description-extra="$t('dashboard.dashboard_empty_text2')"
          :function-button="false"
          :reload-button="false"
          :title="$t('dashboard.dashboard_empty_title')"
          title-image="img/undraw_maker_launch_re_rq81.svg"
        />
      </v-row>
    </v-col>
    <onboarding-success-dialog v-if="onboardingSuccess" @close="successDialogClose"/>
  </hb-basic-page>
</template>

<script>
import CompanyCultureChart from '@/components/Tenant/Dashboard/CompanyCultureChart';
import BasicButton from '@/components/BasicButton';
import CompanyFitnessIndexChart from '@/components/Tenant/Dashboard/CompanyFitnessIndexChart';
import surveyApi from '@/api/survey';
import notificationApi from '@/api/notification';
import SectionHeader from '@/components/SectionHeader';
import NoContentView from '@/components/NoContentView';
import Notification from '@/components/Notification';
import orderBy from 'lodash.orderby';
import FinancialDevelopmentChart from '@/components/Tenant/Dashboard/FinancialDevelopmentChart';
import TasksOverview from '@/components/Tenant/Dashboard/TasksOverview';
import OnboardingSuccessDialog from "@/components/NewAccount/OnboardingSuccessDialog.vue";

export default {
  name: 'DashboardView',
  components: {
    OnboardingSuccessDialog,
    TasksOverview,
    FinancialDevelopmentChart,
    Notification,
    NoContentView,
    SectionHeader,
    CompanyFitnessIndexChart,
    BasicButton,
    CompanyCultureChart,
  },
  props: ['id', 'onboardingSuccess'],
  data() {
    return {
      loading: true,
      subtleLoading: false,
      calculationLoading: false,
      surveys: [],
      notifications: [],
      totalTasksCount: 0,
      undoneTasksCount: 0,
      appBarColorWhite: false,
    };
  },
  computed: {
    personnelSurvey() {
      return this.surveys.find(s => s.type === 'PERSONNEL');
    },
    filteredNotifications() {
      return this.notifications.filter(n => n._show === true).slice(0, 3);
    },
    personnelSurveyHasAnswers() {
      return this.personnelSurvey?.calculation?.engineResponse?.metadata?.answers > 0;
    },
  },
  methods: {
    handleScroll() {
      if (this.$refs.contentContainer) {
        this.appBarColorWhite = this.$refs.contentContainer.getBoundingClientRect().y < 0;
      } else {
        this.appBarColorWhite = false;
      }
    },
    successDialogClose() {
      this.$store.dispatch('reloadMenuCount');
    },
    handleRefresh() {
      this.updateTotalTasksCount();
      this.updateUndoneTasksCount();
    },
    updateTotalTasksCount() {
      if (this.$refs.tasksOverview) {
        this.totalTasksCount = this.$refs.tasksOverview.getTotalTasksCount();
      }
    },
    updateUndoneTasksCount() {
      if (this.$refs.tasksOverview) {
        this.undoneTasksCount = this.$refs.tasksOverview.getUndoneTasksCount();
      }
    },
    goToSurveys() {
      this.$router.push({name: 'tenant_surveys'});
    },
    goToCulture() {
      this.$router.push({
        name: 'tenant_report_category',
        params: {
          category: 'culture',
        },
      });
    },
    goToInputMap() {
      this.$router.push({
        name: 'tenant_reports',
        params: {
          inputMap: true,
        },
      });
    },
    goToTasks() {
      this.$router.push({name: 'tenant_tasks'});
    },
    async reload() {
      this.calculationLoading = true;
      try {
        this.surveys = await surveyApi.getSurveyDashboards();
      } catch (e) {
        this.$handleApiError(e);
      }
      this.calculationLoading = false;
    },
    async calculate() {
      this.calculationLoading = true;
      try {
        const result = await surveyApi.calculateResult(this.personnelSurvey.key);

        this.calculateResult = result;
      } catch (e) {
        console.error(e);
      }
      this.reload();
      this.calculationLoading = false;
    },
    async getNotifications() {
      try {
        this.notifications = orderBy(
          (await notificationApi.getNotifications()).map(n => {
            n._show = true;
            return n;
          }),
          ['createdAt'],
          ['desc']
        );
      } catch (e) {
        console.error(e);
      }
    },
    onNotificationRead(id) {
      const index = this.notifications.findIndex(n => n.id === id);
      if (index > -1) {
        this.notifications[index]._show = false;
      }
    },
  },
  async mounted() {
    await this.getNotifications();
    await this.reload();
    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
.alerts {
  position: relative;
  overflow: hidden;
  margin-top: -64px;
  @media #{map-get($display-breakpoints,'md-and-up')} {
    margin-top: -76px;
  }

  background-image: url('/img/email-pattern.png');
  background-repeat: repeat;

  /* background-image: url('/img/2h-media-TkHbz4Yk348-unsplash.jpg');
  background-size: cover;
  background-position-y: -300px;
   */

  /*
  &:before {
    content: "";
    width: 60%;
    height: 40px;
    opacity: 0.05;
    border-radius: 50%;
    background-color: #fff;
    position: absolute;
    left: 20%;
    bottom: -50px;
    box-shadow: 0 0 100px 60px #001dd0,
    0 0 140px 90px #00cbaf;
  }

   */
}
</style>

